/**
 * Real-time Notifications Context
 * Provides WebSocket notifications throughout the app
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useWebSocket, WS_MESSAGE_TYPES } from '../hooks/useWebSocket';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { Gavel, MessageCircle, Package, Clock, AlertTriangle, Flag, CheckCircle } from 'lucide-react';

const NotificationsContext = createContext(null);

// Custom toast with icon
const showToast = (type, title, description, onClick) => {
  const icons = {
    bid: <Gavel className="w-5 h-5 text-blue-500" />,
    outbid: <AlertTriangle className="w-5 h-5 text-orange-500" />,
    message: <MessageCircle className="w-5 h-5 text-green-500" />,
    order: <Package className="w-5 h-5 text-purple-500" />,
    auction: <Clock className="w-5 h-5 text-yellow-500" />,
    report: <Flag className="w-5 h-5 text-red-500" />,
    success: <CheckCircle className="w-5 h-5 text-green-500" />,
  };

  toast(title, {
    description,
    icon: icons[type] || icons.success,
    action: onClick ? {
      label: 'Vezi',
      onClick,
    } : undefined,
    duration: 5000,
  });
};

// Audio context for notification sounds
let notificationAudioContext = null;

// Play notification sound (different from message beep)
const playNotificationSound = () => {
  try {
    if (!notificationAudioContext) {
      notificationAudioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = notificationAudioContext;
    
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    // Two-tone notification sound (ding-dong effect)
    const now = ctx.currentTime;
    
    // First tone (higher)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.frequency.setValueAtTime(1200, now); // Higher pitch
    osc1.type = 'sine';
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.25, now + 0.02);
    gain1.gain.linearRampToValueAtTime(0, now + 0.15);
    osc1.start(now);
    osc1.stop(now + 0.15);
    
    // Second tone (lower, slight delay)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.setValueAtTime(900, now + 0.1); // Lower pitch
    osc2.type = 'sine';
    gain2.gain.setValueAtTime(0, now + 0.1);
    gain2.gain.linearRampToValueAtTime(0.2, now + 0.12);
    gain2.gain.linearRampToValueAtTime(0, now + 0.3);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.3);
  } catch (error) {
    console.log('Could not play notification sound:', error);
  }
};

export const NotificationsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastPollTime, setLastPollTime] = useState(null);
  
  const { isConnected, lastMessage } = useWebSocket(
    isAuthenticated ? user?.user_id : null
  );

  // Load notifications from API
  const loadNotifications = useCallback(async (checkForNew = false) => {
    if (!isAuthenticated) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/my`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const notificationsList = Array.isArray(data) ? data : (data.notifications || []);
        const formattedNotifications = notificationsList.map(n => ({
          id: n.notification_id || n.id || Math.random().toString(),
          type: n.type,
          title: n.title,
          message: n.message,
          link: n.link,
          image: n.image,
          read: n.read || false,
          timestamp: n.timestamp || n.created_at || new Date().toISOString()
        }));
        
        // Check for new notifications when polling
        if (checkForNew && lastPollTime) {
          const newNotifs = formattedNotifications.filter(n => 
            new Date(n.timestamp) > new Date(lastPollTime) && !n.read
          );
          if (newNotifs.length > 0) {
            // Play notification sound for new notifications
            playNotificationSound();
            newNotifs.forEach(n => {
              showToast('success', n.title, n.message, n.link ? () => window.location.href = n.link : undefined);
            });
          }
        }
        
        setNotifications(formattedNotifications);
        setUnreadCount(formattedNotifications.filter(n => !n.read).length);
        setLastPollTime(new Date().toISOString());
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  }, [isAuthenticated, lastPollTime]);

  // Initial load on mount
  useEffect(() => {
    loadNotifications();
  }, [isAuthenticated]);

  // Polling fallback when WebSocket is not connected
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // If WebSocket is connected, no need for polling
    if (isConnected) {
      console.log('[Notifications] WebSocket connected, polling disabled');
      return;
    }
    
    console.log('[Notifications] WebSocket not connected, enabling polling fallback');
    
    // Poll every 5 seconds when WebSocket is down for faster favicon update
    const pollInterval = setInterval(() => {
      loadNotifications(true);
    }, 5000);
    
    return () => clearInterval(pollInterval);
  }, [isAuthenticated, isConnected, loadNotifications]);

  // Add notification function - defined before being used in useEffect
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: `notif_${Date.now()}`,
      timestamp: new Date().toISOString(),
      read: false,
      ...notification,
    };
    
    // Play notification sound (except for messages - they have their own sound in MessagesPage)
    if (notification.type !== 'message') {
      playNotificationSound();
    }
    
    // Keep up to 200 notifications for pagination support
    setNotifications(prev => [newNotification, ...prev].slice(0, 200));
    setUnreadCount(prev => prev + 1);
  }, []);

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (!lastMessage) return;
    
    const { type, data } = lastMessage;
    
    switch (type) {
      case WS_MESSAGE_TYPES.NEW_BID:
        showToast('bid', `Ofertă nouă: ${data.amount} RON`, 
          `${data.bidder_username} a licitat la "${data.listing_title}"${data.time_extended ? ' (+5 min)' : ''}`,
          () => window.location.href = `/listing/${data.listing_slug || data.listing_id}`
        );
        addNotification({
          type: 'new_bid',
          title: 'Ofertă nouă',
          message: `${data.bidder_username} a licitat ${data.amount} RON la "${data.listing_title}"`,
          link: `/listing/${data.listing_slug || data.listing_id}`,
          data,
        });
        break;
        
      case WS_MESSAGE_TYPES.OUTBID:
        showToast('outbid', 'Ai fost depășit!', 
          `Cineva a licitat ${data.new_amount} RON la "${data.listing_title}"`,
          () => window.location.href = `/listing/${data.listing_slug || data.listing_id}`
        );
        addNotification({
          type: 'outbid',
          title: 'Ai fost depășit',
          message: `Oferta ta de ${data.your_amount} RON a fost depășită cu ${data.new_amount} RON`,
          link: `/listing/${data.listing_slug || data.listing_id}`,
          data,
        });
        break;
        
      case WS_MESSAGE_TYPES.NEW_MESSAGE:
        // Add notification for new messages
        showToast('message', `Mesaj de la ${data.sender_name || data.sender_username}`, 
          data.content.length > 50 ? data.content.substring(0, 50) + '...' : data.content,
          () => window.location.href = `/messages/${data.conversation_id}`
        );
        addNotification({
          type: 'message',
          title: 'Mesaj nou',
          message: `${data.sender_name || data.sender_username}: ${data.content.length > 50 ? data.content.substring(0, 50) + '...' : data.content}`,
          link: `/messages/${data.conversation_id}`,
          data,
        });
        break;
        
      case WS_MESSAGE_TYPES.ORDER_UPDATE:
        const statusLabels = {
          paid: 'plătită',
          shipped: 'expediată',
          delivered: 'livrată',
          completed: 'finalizată',
          cancelled: 'anulată',
          refunded: 'rambursată',
        };
        showToast('order', 'Actualizare comandă', 
          `Comanda #${data.order_id?.slice(-8)} a fost ${statusLabels[data.status] || data.status}`,
          () => window.location.href = `/order/${data.order_id}`
        );
        addNotification({
          type: 'order_update',
          title: 'Actualizare comandă',
          message: `Comanda #${data.order_id?.slice(-8)} - ${statusLabels[data.status] || data.status}`,
          link: `/order/${data.order_id}`,
          data,
        });
        break;
        
      case WS_MESSAGE_TYPES.AUCTION_ENDING:
        showToast('auction', 'Licitație pe final!', 
          `"${data.listing_title}" se termină în ${data.minutes_remaining} minute`,
          () => window.location.href = `/listing/${data.listing_slug || data.listing_id}`
        );
        addNotification({
          type: 'auction_ending',
          title: 'Licitație pe final',
          message: `"${data.listing_title}" se termină în curând`,
          link: `/listing/${data.listing_slug || data.listing_id}`,
          data,
        });
        break;
        
      case WS_MESSAGE_TYPES.AUCTION_ENDED:
        showToast('auction', 'Licitație încheiată', 
          `"${data.listing_title}" s-a încheiat${data.winner ? ` - Câștigător: ${data.winner}` : ''}`,
          () => window.location.href = `/listing/${data.listing_slug || data.listing_id}`
        );
        addNotification({
          type: 'auction_ended',
          title: 'Licitație încheiată',
          message: `"${data.listing_title}" s-a încheiat`,
          link: `/listing/${data.listing_slug || data.listing_id}`,
          data,
        });
        break;
        
      case 'new_report':
        // Admin-only notification for new reports
        if (user?.role === 'admin' || user?.role === 'moderator') {
          showToast('report', 'Raport nou', 
            data.description?.substring(0, 50) || 'Un utilizator a trimis un raport',
            () => window.location.href = '/admin?view=disputes'
          );
          addNotification({
            type: 'new_report',
            title: 'Raport nou',
            message: data.description || 'Raport de la un utilizator',
            link: '/admin?view=disputes',
            data,
          });
        }
        break;
        
      case 'time_extended':
        showToast('auction', 'Timp prelungit!', 
          `Licitația "${data.listing_title}" a fost prelungită cu 5 minute`,
          () => window.location.href = `/listing/${data.listing_slug || data.listing_id}`
        );
        addNotification({
          type: 'time_extended',
          title: 'Timp prelungit',
          message: `Licitația a fost prelungită datorită unei oferte de ultim moment`,
          link: `/listing/${data.listing_slug || data.listing_id}`,
          data,
        });
        break;
        
      default:
        break;
    }
  }, [lastMessage, user?.role, addNotification]);

  const markAsRead = useCallback(async (notificationId) => {
    // First, check if already read to avoid double-counting
    const notification = notifications.find(n => n.id === notificationId);
    if (notification?.read) return;
    
    // Optimistically update UI
    setNotifications(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Persist to backend
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}/read`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [notifications]);

  const markAllAsRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    
    // Persist to backend
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-read`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  const clearNotifications = useCallback(async () => {
    // Optimistically clear UI
    setNotifications([]);
    setUnreadCount(0);
    
    // Persist to backend
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/all`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }, []);

  const removeNotification = useCallback(async (notificationId) => {
    // Optimistically update UI
    setNotifications(prev => {
      const notification = prev.find(n => n.id === notificationId || n.notification_id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(count => Math.max(0, count - 1));
      }
      return prev.filter(n => n.id !== notificationId && n.notification_id !== notificationId);
    });
    
    // Call backend to persist deletion
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, []);

  // Refresh notifications from API
  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/my?limit=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const notificationsList = Array.isArray(data) ? data : (data.notifications || []);
        const formattedNotifications = notificationsList.map(n => ({
          id: n.notification_id || n.id || Math.random().toString(),
          notification_id: n.notification_id,
          type: n.type,
          title: n.title,
          message: n.message,
          link: n.link,
          read: n.read || false,
          timestamp: n.timestamp || n.created_at || new Date().toISOString()
        }));
        setNotifications(formattedNotifications);
        setUnreadCount(formattedNotifications.filter(n => !n.read).length);
      }
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    }
  }, [isAuthenticated]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        isConnected,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotifications,
        removeNotification,
        refreshNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export default NotificationsContext;
